/**
 * Resize and rotate math.
 *
 * The important part: resizing holds the OPPOSITE corner fixed in world space
 * and solves for the new centre. Keeping x/y and only changing width/height
 * makes a rotated element walk across the canvas, because rotation pivots about
 * the centre and the centre moved.
 */

import type { Point, SlateElement } from '../scene/types';
import { isLinear } from '../scene/types';
import { anchorLocalPoint, type ResizeHandle } from './hit';
import { add, rotateVec, toLocal, toWorld, type Vec } from './transform';

export const MIN_ELEMENT_SIZE = 2;

export interface ResizeResult {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly points?: readonly Point[];
  /** The handle after any flip, so a continuing drag stays on the same corner. */
  readonly handle: ResizeHandle;
}

function flipHorizontal(handle: ResizeHandle): ResizeHandle {
  if (handle.includes('e')) return handle.replace('e', 'w') as ResizeHandle;
  if (handle.includes('w')) return handle.replace('w', 'e') as ResizeHandle;
  return handle;
}

function flipVertical(handle: ResizeHandle): ResizeHandle {
  if (handle.includes('n')) return handle.replace('n', 's') as ResizeHandle;
  if (handle.includes('s')) return handle.replace('s', 'n') as ResizeHandle;
  return handle;
}

export function resizeElement(
  el: SlateElement,
  handle: ResizeHandle,
  pointerWorld: Vec,
  opts: { keepAspect?: boolean; minSize?: number } = {},
): ResizeResult {
  const minSize = opts.minSize ?? MIN_ELEMENT_SIZE;

  // 1. The world point that must not move, captured from the BEFORE geometry.
  const anchorWorld = toWorld(anchorLocalPoint(handle, el.width, el.height), el);

  // 2. Pointer in the BEFORE local frame.
  const local = toLocal(pointerWorld, el);

  // 3. Signed sizes along whichever axes this handle drives.
  let rawW = handle.includes('e') ? local.x : handle.includes('w') ? el.width - local.x : el.width;
  let rawH = handle.includes('n') ? el.height - local.y : handle.includes('s') ? local.y : el.height;

  if (opts.keepAspect && el.width > 0 && el.height > 0) {
    const sx = rawW / el.width;
    const sy = rawH / el.height;
    const s = Math.max(Math.abs(sx), Math.abs(sy));
    rawW = el.width * s * (sx < 0 ? -1 : 1);
    rawH = el.height * s * (sy < 0 ? -1 : 1);
  }

  // Negative drags are normalized here and never reach the model: a negative
  // width means an invalid <rect>, a wrong rotation pivot, and NaN when scaling
  // points.
  let nextHandle = handle;
  if (rawW < 0) nextHandle = flipHorizontal(nextHandle);
  if (rawH < 0) nextHandle = flipVertical(nextHandle);

  const signX = rawW < 0 ? -1 : 1;
  const signY = rawH < 0 ? -1 : 1;
  const absW = Math.max(minSize, Math.abs(rawW));
  const absH = Math.max(minSize, Math.abs(rawH));

  // 4/5. Solve for the centre that keeps the anchor pinned.
  const anchorNew = anchorLocalPoint(nextHandle, absW, absH);
  const offset = rotateVec({ x: absW / 2 - anchorNew.x, y: absH / 2 - anchorNew.y }, el.angle);
  const center = add(anchorWorld, offset);

  const result: ResizeResult = {
    x: center.x - absW / 2,
    y: center.y - absH / 2,
    width: absW,
    height: absH,
    handle: nextHandle,
  };

  if (!isLinear(el)) return result;

  // Guard the divisor: a zero-height freedraw (a straight horizontal stroke) is
  // perfectly legal.
  const scaleX = (absW * signX) / (el.width || 1);
  const scaleY = (absH * signY) / (el.height || 1);
  const points = el.points.map(
    ([px, py]) =>
      [
        px * scaleX + (scaleX < 0 ? absW : 0),
        py * scaleY + (scaleY < 0 ? absH : 0),
      ] as Point,
  );
  return { ...result, points };
}

/** Drag one point of a linear element. Points stay authoritative. */
export function moveLinearPoint(
  el: SlateElement,
  index: number,
  pointerWorld: Vec,
): readonly Point[] | null {
  if (!isLinear(el)) return null;
  if (index < 0 || index >= el.points.length) return null;
  const local = toLocal(pointerWorld, el);
  const next = el.points.slice();
  next[index] = [local.x, local.y];
  return next;
}

export const ROTATE_SNAP_RADIANS = (15 * Math.PI) / 180;

/**
 * Angle grab offset captured on pointer down, so the element does not jump to
 * the pointer when the drag starts.
 */
export function rotationGrabOffset(el: SlateElement, pointerWorld: Vec): number {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  return Math.atan2(pointerWorld.y - cy, pointerWorld.x - cx) - el.angle;
}

export function rotateElement(
  el: SlateElement,
  pointerWorld: Vec,
  grabOffset: number,
  snap: boolean,
): number {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  let angle = Math.atan2(pointerWorld.y - cy, pointerWorld.x - cx) - grabOffset;
  if (snap) angle = Math.round(angle / ROTATE_SNAP_RADIANS) * ROTATE_SNAP_RADIANS;
  const twoPi = Math.PI * 2;
  return ((angle % twoPi) + twoPi) % twoPi;
}
